// ═══════════════════════════════════════════════════════════
// WIZARD CONTENT MODEL
// All step data, options, explainers, and metadata for the
// PII Finder & DPDP Impact Check wizard. Edit here to add
// industries, activities, data types, or DPDP explainers.
// ═══════════════════════════════════════════════════════════

import {
  Landmark, Stethoscope, Cpu, Factory, ShoppingCart,
  GraduationCap, Truck, MapPin, Briefcase,
  UserCheck, Users, UserPlus, MonitorSmartphone, Baby,
  Megaphone, Receipt, Heart, Handshake,
  Lock, CreditCard, Fingerprint, Globe,
  ClipboardList, Cloud, Server, Table2, MessageSquare,
  Building2, Database, HardDrive, BarChart3
} from 'lucide-react';

// ──────────────────────────────────────────────────────────
// STEP METADATA (progress bar labels)
// ──────────────────────────────────────────────────────────

export const STEP_META = [
  { label: 'Industry', icon: Building2 },
  { label: 'Activities', icon: ClipboardList },
  { label: 'Subjects', icon: Users },
  { label: 'Data Types', icon: Database },
  { label: 'Systems', icon: HardDrive },
  { label: 'Results', icon: BarChart3 },
];

// ──────────────────────────────────────────────────────────
// STEP 1: INDUSTRIES
// ──────────────────────────────────────────────────────────

export const INDUSTRIES = [
  { id: 'bfsi', label: 'Banking, Financial Services & Insurance', icon: Landmark },
  { id: 'healthcare', label: 'Healthcare & Pharma', icon: Stethoscope },
  { id: 'it_saas', label: 'IT / SaaS / Technology', icon: Cpu },
  { id: 'manufacturing', label: 'Manufacturing & Industrial', icon: Factory },
  { id: 'retail', label: 'Retail / E-commerce / D2C', icon: ShoppingCart },
  { id: 'education', label: 'Education & EdTech', icon: GraduationCap },
  { id: 'logistics', label: 'Logistics & Supply Chain', icon: Truck },
  { id: 'real_estate', label: 'Real Estate & Property', icon: MapPin },
  { id: 'professional_services', label: 'Professional Services', icon: Briefcase },
];

// ──────────────────────────────────────────────────────────
// STEP 2: BUSINESS ACTIVITIES
// ──────────────────────────────────────────────────────────

export const BUSINESS_ACTIVITIES = [
  { id: 'customer_kyc', label: 'Customer onboarding & KYC', example: 'e.g. collecting Aadhaar, PAN during account opening', icon: UserCheck },
  { id: 'employee_payroll', label: 'Employee hiring & payroll', example: 'e.g. offer letters, salary processing, PF/ESI filings', icon: Users },
  { id: 'marketing', label: 'Marketing campaigns & analytics', example: 'e.g. email lists, website tracking, ad targeting', icon: Megaphone },
  { id: 'payments', label: 'Online payments & billing', example: 'e.g. UPI collection, invoice processing, refunds', icon: Receipt },
  { id: 'loyalty', label: 'Loyalty programs & customer profiling', example: 'e.g. reward points, purchase history analysis', icon: Heart },
  { id: 'vendor_mgmt', label: 'Vendor & contractor management', example: 'e.g. vendor onboarding, GST verification, payments', icon: Handshake },
];

// ──────────────────────────────────────────────────────────
// STEP 3: DATA SUBJECTS
// ──────────────────────────────────────────────────────────

export const DATA_SUBJECTS = [
  { id: 'customers', label: 'Customers / End Users', desc: 'People who buy your product or use your service', icon: Users },
  { id: 'employees', label: 'Employees & Contract Workers', desc: 'Full-time, part-time, consultants on your payroll', icon: UserCheck },
  { id: 'applicants', label: 'Job Applicants', desc: 'Candidates who applied for open positions', icon: UserPlus },
  { id: 'vendors', label: 'Vendors & Suppliers', desc: 'Third-party businesses and their contact persons', icon: Truck },
  { id: 'visitors', label: 'Website / App Visitors', desc: 'Anyone who visits your digital properties', icon: MonitorSmartphone },
  { id: 'children', label: 'Children (under 18)', desc: 'Minors who may interact with your services', icon: Baby, tag: 'SDF TRIGGER', tagColor: 'red' },
];

// ──────────────────────────────────────────────────────────
// STEP 4: DATA ELEMENTS
// ──────────────────────────────────────────────────────────

export const DATA_ELEMENTS = [
  { id: 'contact_info', label: 'Names, phone, email, address', example: 'Basic identity and contact details', icon: Users, risk: 'standard' },
  { id: 'gov_ids', label: 'Aadhaar, PAN, passport, DL', example: 'Government-issued identity numbers', icon: Lock, risk: 'critical' },
  { id: 'financial', label: 'Bank accounts & payment cards', example: 'Account numbers, UPI IDs, card details', icon: CreditCard, risk: 'high' },
  { id: 'health', label: 'Health & medical records', example: 'Diagnosis, prescriptions, insurance claims', icon: Stethoscope, risk: 'critical' },
  { id: 'biometric', label: 'Biometric data', example: 'Fingerprints, facial scans, iris data', icon: Fingerprint, risk: 'critical' },
  { id: 'digital', label: 'Location & device data', example: 'IP addresses, GPS, cookies, device IDs', icon: Globe, risk: 'standard' },
];

// ──────────────────────────────────────────────────────────
// STEP 5: SYSTEMS & STORAGE
// ──────────────────────────────────────────────────────────

export const SYSTEMS = [
  { id: 'crm', label: 'CRM tools', example: 'Salesforce, HubSpot, Zoho, Freshsales', icon: ClipboardList },
  { id: 'hrms', label: 'HRMS / Payroll systems', example: 'Darwinbox, Keka, GreytHR, Workday', icon: Users },
  { id: 'cloud', label: 'Cloud platforms', example: 'AWS, Azure, Google Cloud, DigitalOcean', icon: Cloud },
  { id: 'saas', label: 'Third-party SaaS tools', example: 'Analytics, email marketing, chat, helpdesk', icon: MessageSquare },
  { id: 'onprem', label: 'On-premise servers & databases', example: 'Local SQL databases, file servers, NAS', icon: Server },
  { id: 'sheets', label: 'Spreadsheets & shared drives', example: 'Google Sheets, Excel files, shared folders', icon: Table2 },
];

// ──────────────────────────────────────────────────────────
// DPDP EXPLAINERS (one per selection step)
// ──────────────────────────────────────────────────────────

export const DPDP_EXPLAINERS = [
  'DPDP classifies obligations by industry risk. Healthcare, BFSI, and EdTech organisations handling large volumes of data may be designated as Significant Data Fiduciaries with stricter duties.',
  'Each business activity that collects or processes personal data creates a separate compliance obligation under DPDP — from consent to retention to deletion.',
  'DPDP defines anyone whose data you process as a "Data Principal." You owe specific rights to each category — access, correction, erasure, and grievance redressal.',
  'The type of personal data you handle determines your risk tier. Government IDs, health data, and biometrics trigger the highest compliance requirements under DPDP.',
  'Every system that stores or processes personal data is a potential audit point. DPDP requires you to demonstrate purpose-limited, secure, and time-bound storage across all systems.',
];

// ──────────────────────────────────────────────────────────
// STEP TITLES & DESCRIPTIONS
// ──────────────────────────────────────────────────────────

export const STEP_COPY = {
  industry: {
    title: 'What industry is your organisation in?',
    description: 'Pick the closest match. This helps us calibrate your regulatory exposure.',
  },
  activities: {
    title: "Which business activities involve people's data?",
    description: 'Select all that apply. Each activity creates a separate compliance touchpoint.',
  },
  subjects: {
    title: "Whose personal data does your organisation handle?",
    description: 'Think about every group of people whose information you collect, store, or share.',
  },
  elements: {
    title: 'What types of personal data do you collect?',
    description: 'Select every category that applies — even if stored in just one system.',
  },
  systems: {
    title: 'Where is this data stored or processed?',
    description: 'Include every tool, platform, or location where personal data lives — even temporarily.',
  },
};

// ──────────────────────────────────────────────────────────
// WIZARD UI LABELS
// ──────────────────────────────────────────────────────────

export const WIZARD_LABELS = {
  headerBadge: 'PII Finder & DPDP Impact Check',
  analyzingTitle: 'Analysing Your Data Landscape',
  analyzingDescription: 'Running inference rules across your selections, mapping to DPDP Act 2023 and Rules 2025...',
  nextButton: 'Continue',
  analyzeButton: 'Analyse My Data',
  backButton: 'Back',
};
